<template>
  <Loading
    v-if="composable.loading.setting.isShow"
    :content="composable.loading.setting.content"
  />

  <div
    v-if="page === 'initialization'"
    class="page"
  />

  <div
    v-else-if="page==='showing'"
    class="page"
  >
    <div class="mt2 mb4 fs5 fw4 fc--primary">
      {{ clinicInfo.fullname }}
    </div>
    <div class="page__is-expanded bc--secondary">
      <div class="p4 row--scroll-x">
        <div
          v-for="division in clinicInfo.divisions"
          :key="division"
          :class="{
            'bc--primary': choice.division === division,
            'fc--white': choice.division === division,
            'fc--secondary': choice.division !== division,
          }"
          @click="choice.division = division"
          class="label ph4"
        >
          {{ division }}
        </div>
      </div>
    </div>
    <div class="page__is-expanded bc--secondary mb4">
      <div class="pv3 ph4 row--scroll-x">
        <div
          :class="{
            'bc--primary': choice.week === 0,
            'fc--white': choice.week === 0,
            'fc--secondary': choice.week !== 0,
          }"
          @click="choice.week = 0"
          class="label ph3"
        >
          一週內
        </div>
        <div
          :class="{
            'bc--primary': choice.week === 1,
            'fc--white': choice.week === 1,
            'fc--secondary': choice.week !== 1,
          }"
          @click="choice.week = 1"
          class="label ph3"
        >
          下一週
        </div>
        <div
          :class="{
            'bc--primary': choice.week === 2,
            'fc--white': choice.week === 2,
            'fc--secondary': choice.week !== 2,
          }"
          @click="choice.week = 2"
          class="label ph3"
        >
          兩週後
        </div>
        <div
          :class="{
            'bc--primary': choice.week === 3,
            'fc--white': choice.week === 3,
            'fc--secondary': choice.week !== 3,
          }"
          @click="choice.week = 3"
          class="label ph3"
        >
          三週後
        </div>
      </div>
      <div class="bc--secondary pb3 ph4 row--scroll-x">
        <div
          v-for="(day,date) in datesInWeek"
          :key="date"
          :class="{
            'bc--primary': choice.date === date,
            'fc--white': choice.date === date,
            'fc--secondary': choice.date !== date,
          }"
          @click="choice.date = date"
          class="circle circle--size-6 fs1"
        >
          <div>
            {{ spiderman.moment(date).format('MM/DD') }}
          </div>
          <div>
            ({{ spiderman.moment(date).locale('zh_cn').format('dd') }})
          </div>
        </div>
      </div>
    </div>
    <Shift
      v-for="shift in shifts"
      :key="shift"
      :shift="shift"
      @click="chooseShift(shift)"
      class="mb3"
    />
  </div>

  <Making
    v-else-if="page === 'making'"
    @cancel="page = 'showing'"
    :clinic-info="clinicInfo"
    :shift="chosenShift"
    v-model:modelMemberInfo="memberInfo"
    @makeAppointment="makeAppointment"
    @clearMemberInfo="clearMemberInfo"
  />

  <Death
    v-else
    :message="composable.death.message"
    :page="page"
  />

  <Modal
    v-if="composable.modal.setting.isShow"
    :setting="composable.modal.setting"
    @close="composable.modal.close"
    @toShowing="toShowing"
    @done="liff.closeWindow"
  />
</template>

<script>
import {
  reactive, ref, onMounted, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import spiderman from '@/spiderman/index';
import api from '@/api/index';
import composable from '@/composable/index';
import Shift from './components/Shift.vue';
import Making from './pages/Making.vue';

export default {
  components: { Making, Shift },
  setup() {
    const route = useRoute();
    const page = ref('initialization');
    let schedule = [];
    const clinicInfo = ref(null);
    const choice = reactive({
      division: '',
      week: 0,
      date: '',
    });
    const datesInWeek = ref({});
    const shifts = ref([]);
    const chosenShift = ref(null);
    const memberInfo = ref({
      name: '',
      id: '',
      birthdate: '',
      mobile: '',
    });

    onMounted(async () => {
      try {
        composable.loading.show('資料載入中');

        const { clinic } = route.params;
        if (clinic === 'heart') {
          await composable.member.initialize({
            liffId: '1656042583-Oe7BqAE4',
            clinic: '金安心',
          });
        } else if (clinic === 'beauty') {
          await composable.member.initialize({
            liffId: '1656042583-8p1w3NJr',
            clinic: '金禾美',
          });
        }
        clinicInfo.value = spiderman.clinic[composable.member.info.clinic];

        schedule = await api.appointments.getSchedule(composable.member);
        [choice.division] = clinicInfo.value.divisions;

        page.value = 'showing';
      } catch (error) {
        page.value = 'death';
        composable.death.show(error);
      } finally {
        composable.loading.close();
      }
    });

    watch(
      () => choice.division,
      () => {
        if (choice.week !== 0) {
          choice.week = 0;
          return;
        }
        datesInWeek.value = getDatesInWeek();
        if (choice.date !== Object.keys(datesInWeek.value)[0]) {
          [choice.date] = Object.keys(datesInWeek.value);
          return;
        }
        shifts.value = getShifts();
      },
      { deep: true },
    );

    function getDatesInWeek() {
      const dates = {};
      schedule.forEach((aShift) => {
        if ((aShift.week === choice.week)
          && (aShift.division === choice.division)) {
          dates[aShift.date] = aShift.zhDayOfWeek;
        }
      });

      return dates;
    }

    function getShifts() {
      return schedule.filter(
        (aShift) => (aShift.division === choice.division)
          && (aShift.week === choice.week)
          && (aShift.date === choice.date),
      );
    }

    watch(
      () => choice.week,
      () => {
        datesInWeek.value = getDatesInWeek();
        [choice.date] = Object.keys(datesInWeek.value);
      },
      { deep: true },
    );

    watch(
      () => choice.date,
      () => {
        shifts.value = getShifts();
      },
      { deep: true },
    );

    function chooseShift(shift) {
      if (!shift.canMakeAppointment) {
        composable.modal.popup('此時段已不開放線上預約，請來電洽詢。');
        return;
      }
      chosenShift.value = shift;
      if (composable.member.info.verified) {
        memberInfo.value = {
          name: composable.member.info.name,
          id: composable.member.info.twid,
          birthdate: composable.member.info.birthdate,
          mobile: composable.member.info.mobile,
        };
      }
      page.value = 'making';
    }

    function clearMemberInfo() {
      memberInfo.value = {
        name: '',
        id: '',
        birthdate: '',
        mobile: '',
      };
    }

    async function makeAppointment() {
      try {
        composable.loading.show('預約掛號中，請稍候');

        spiderman.form.verify(memberInfo.value);
        memberInfo.value.birthdate = spiderman
          .moment(memberInfo.value.birthdate)
          .format('YYYY-MM-DD');

        const response = await api.appointments.make(
          composable.member, memberInfo.value, chosenShift.value,
        );

        composable.modal.show({
          body: `${response.message}。 掛號序號：${response?.appointment?.掛號序號}\n看診當日請先至櫃檯報到。`,
          hasCloseButton: false,
          footer: [
            { content: '繼續預約', type: 'secondary', onClickEmit: 'toShowing' },
            { content: '關閉視窗', type: 'primary', onClickEmit: 'done' },
          ],
        });
      } catch (error) {
        composable.modal.alert(error);
      } finally {
        composable.loading.close();
      }
    }

    function toShowing() {
      page.value = 'showing';
      composable.modal.close();
    }

    return {
      liff: spiderman.liff,
      page,
      composable,
      spiderman,
      clinicInfo,
      choice,
      datesInWeek,
      shifts,
      chooseShift,
      chosenShift,
      memberInfo,
      clearMemberInfo,
      makeAppointment,
      toShowing,
    };
  },
};
</script>
